import { Material } from "@/types/material/material.types";
import { Column } from "@/components/ui/data-table/types";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Pencil, Trash2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { formatNumberDynamic } from "@/utils/numberFormatter";

export const useMaterialColumns = (
  t: (key: string) => string,
  onEdit: (material: Material) => void,
  onDelete: (material: Material) => void
): Column<Material>[] => {
  const { claims } = useAuth();
  const isAssimagraUser = claims?.role === "assimagra";
  
  const getCarbonFactorsDisplay = (material: Material): JSX.Element => {
    if (!material.carbon_factors || !material.units) {
      return <div className="text-gray-400">-</div>;
    }
    
    const factors: JSX.Element[] = [];
    material.units.forEach((unit, index) => {
      const dbUnit = unit === "unit" ? "unit" : unit;
      const factor = material.carbon_factors?.[dbUnit as keyof typeof material.carbon_factors];
      if (factor?.value !== undefined) {
        const displayUnit = unit === "unit" ? "un" : unit;
        factors.push(
          <div key={index} className="mb-1 last:mb-0">
            <span className="font-medium text-gray-900">{formatNumberDynamic(factor.value)}</span>
            <span className="text-gray-500 ml-1">({displayUnit})</span>
          </div>
        );
      }
    });
    
    return factors.length > 0 ? <div>{factors}</div> : <div className="text-gray-400">-</div>;
  };

  const getHydricFactorsDisplay = (material: Material): JSX.Element => {
    if (!material.hydric_factors || !material.units) {
      return <div className="text-gray-400">-</div>;
    }
    
    const factors: JSX.Element[] = [];
    material.units.forEach((unit, index) => {
      const dbUnit = unit === "unit" ? "unit" : unit;
      const factor = material.hydric_factors?.[dbUnit as keyof typeof material.hydric_factors];
      if (factor?.value !== undefined) {
        const displayUnit = unit === "unit" ? "un" : unit;
        factors.push(
          <div key={index} className="mb-1 last:mb-0">
            <span className="font-medium text-gray-900">{formatNumberDynamic(factor.value)}</span>
            <span className="text-gray-500 ml-1">({displayUnit})</span>
          </div>
        );
      }
    });
    
    return factors.length > 0 ? <div>{factors}</div> : <div className="text-gray-400">-</div>;
  };
  
  const baseColumns: Column<Material>[] = [
    {
      header: t("materials.table.name"),
      accessorKey: "name",
      sortable: true,
      className: "min-w-[180px]",
    },
    {
      header: t("materials.table.units"),
      accessorKey: "units",
      sortable: true,
      className: "min-w-[120px]",
      cell: (_: string, row: Material) => {
        if (!row.units) return <div>-</div>;

        const displayUnits = row.units.map((unit) => {
          return unit === "unit" ? "un" : unit;
        });

        return <div>{displayUnits.join(", ")}</div>;
      },
    },
    {
      header: `${t("materials.table.carbonFactors")} (kg CO₂eq/unidade)`,
      accessorKey: "name",
      sortable: true,
      className: "min-w-[340px]",
      headerClassName: "whitespace-normal break-words leading-tight",
      cell: (_: string, row: Material) => {
        return <div className="text-sm py-2">{getCarbonFactorsDisplay(row)}</div>;
      },
    },
    {
      header: `${t("materials.table.hydricFactors")} (L/unidade)`,
      accessorKey: "name",
      sortable: true,
      className: "min-w-[240px]",
      cell: (_: string, row: Material) => {
        return <div className="text-sm py-2">{getHydricFactorsDisplay(row)}</div>;
      },
    },
  ];

  const actionsColumn: Column<Material> = {
    header: t("materials.table.actions"),
    accessorKey: "id",
    cell: (_: string, row: Material) => {
      return (
        <div className="flex items-center justify-end space-x-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onEdit(row)}
                  className="h-8 w-8 p-0 hover:bg-gray-100"
                >
                  <Pencil className="h-4 w-4 text-gray-500" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top" align="center">
                <p>{t("materials.actions.edit")}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onDelete(row)}
                  className="h-8 w-8 p-0 hover:bg-gray-100"
                >
                  <Trash2 className="h-4 w-4 text-gray-500" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top" align="center">
                <p>{t("materials.actions.delete")}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      );
    },
  };

  return isAssimagraUser ? baseColumns : [...baseColumns, actionsColumn];
};
